import { ISpeaker, ITeam } from 'configs/interfaces/data23';

// Firestore data comes from the CMS sync and is not type-checked. Coerce every field
// to the type the UI expects so a single malformed document cannot crash a screen.

const toStr = (value: unknown): string => {
	if (value === null || value === undefined) {
		return '';
	}
	if (typeof value === 'string') {
		return value;
	}
	if (typeof value === 'number' || typeof value === 'boolean') {
		return String(value);
	}
	return '';
};

const toOptStr = (value: unknown): string | undefined => {
	const str = toStr(value);
	return str === '' ? undefined : str;
};

const toNum = (value: unknown, fallback = 0): number => {
	const num = typeof value === 'number' ? value : Number(value);
	return Number.isFinite(num) ? num : fallback;
};

const toOptNum = (value: unknown): number | undefined => {
	if (value === null || value === undefined || value === '') {
		return undefined;
	}
	const num = Number(value);
	return Number.isFinite(num) ? num : undefined;
};

const toStrArray = (value: unknown): string[] | undefined => {
	if (value === null || value === undefined) {
		return undefined;
	}
	const arr = Array.isArray(value) ? value : [value];
	return arr.map(toStr).filter((e) => e !== '');
};

export const normalizeTeam = (raw: any): ITeam | undefined => {
	if (!raw || typeof raw !== 'object') {
		return undefined;
	}
	if (raw.id === null || raw.id === undefined || raw.id === '') {
		return undefined;
	}
	const id = toOptNum(raw.id) ?? raw.id;

	return {
		...raw,
		id,
		title: toStr(raw.title),
		maxUsers: toNum(raw.maxUsers),
		image: toOptStr(raw.image),
		teamCategory1: toStr(raw.teamCategory1),
		teamCategory2: toStr(raw.teamCategory2),
		teamCategory3: toStrArray(raw.teamCategory3),
		teamCategory4: toStrArray(raw.teamCategory4),
		speaker: toOptNum(raw.speaker),
		row: toOptStr(raw.row),
		table: toOptStr(raw.table),
		order: toOptNum(raw.order),
		isDeleted: !!raw.isDeleted,
	};
};

export const normalizeSpeaker = (raw: any): ISpeaker | undefined => {
	if (!raw || typeof raw !== 'object') {
		return undefined;
	}
	if (raw.id === null || raw.id === undefined || raw.id === '') {
		return undefined;
	}
	const id = toOptNum(raw.id) ?? raw.id;

	return {
		...raw,
		id,
		title: toOptStr(raw.title),
		firstName: toStr(raw.firstName),
		lastName: toStr(raw.lastName),
		jobTitle: toOptStr(raw.jobTitle),
		company: toOptStr(raw.company),
		image: toOptStr(raw.image),
		bio: toOptStr(raw.bio),
		linkedInProfileUrl: toOptStr(raw.linkedInProfileUrl),
		isDeleted: !!raw.isDeleted,
	};
};

export const normalizeList = <T>(list: unknown, normalize: (raw: any) => T | undefined): T[] => {
	if (!Array.isArray(list)) {
		return [];
	}
	return list.map(normalize).filter((e): e is T => e !== undefined);
};
